import React, { useEffect, useRef, useState } from 'react';
import { useFormik } from 'formik';
import Error404 from '@/app/components/Error404';
import usePermission from '@/hooks/usePermission';
import { Link } from 'react-router-dom';
import Loader from '@/app/components/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { t } from 'i18next';
import { useClient } from 'jsonapi-react';
import { BannerItem } from './BannerItem';
import BannerDetailsModal from './BannerDetailsModal';
import { FilterSelectNative } from '@/components/custom/filter';
import EndScroll from '@/app/components/endScroll';
import InfiniteScroll from 'react-infinite-scroll-component';
import { DashboardContent } from '@/layouts/dashboard';

export default function BulletinBoards(props) {
  const { uri } = props;
  const client = useClient();
  const [bannersList, setBannersList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [selectedBanner, setSelectedBanner] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const isInitialMount = useRef(true);

  const pageSize = 10;
  const [hasMore, setHasMore] = useState(true);
  const debounceTimer = useRef(null);

  const viewBanners = usePermission('Visualizar Mural', 'Mural');

  const filters = useFormik({
    initialValues: {
      searchTerm: '',
      profile: '',
      status: ''
    }
  });

  const getBannersList = async () => {
    let url = `bulletin_boards?page[number]=${pageNumber}&page[size]=${pageSize}&direction=desc&sort=created_at`;

    if (filters.values.searchTerm) {
      url += `&q=${filters.values.searchTerm}`;
    }
    if (filters.values.profile && filters.values.profile !== 'blank') {
      url += `&filter[profiles.id]=${filters.values.profile}`;
    }
    if (filters.values.status && filters.values.status !== 'blank') {
      url += `&filter[published]=${filters.values.status === 'published' ? true : false}`;
    }

    setLoading(true);
    try {
      const response = await client.fetch(url);
      setBannersList(pageNumber === 1 ? response.data : [...bannersList, ...response.data]);

      if (response.data.length < pageSize) {
        setHasMore(false);
      }
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  const getProfilesList = async () => {
    try {
      const { data, error } = await client.fetch('/profiles');
      if (error) {
        toast.error(t('warning.errorFetchingProfiles'));
      } else {
        setProfiles(data);
      }
    } catch (error) {
      toast.error(t('warning.errorFetchingProfiles'));
    }
  };

  useEffect(() => {
    getProfilesList();
  }, []);

  useEffect(() => {
    getBannersList();
  }, [pageNumber]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      if (pageNumber === 1) {
        getBannersList();
      } else {
        setPageNumber(1);
      }
      setHasMore(true);
      setBannersList([]);
    }, 500);

    return () => clearTimeout(debounceTimer.current);
  }, [filters.values]);

  const breadcrumbs = {
    title: 'Banners',
    nav: [
      {
        route: uri,
        name: 'Banners informativos',
        isActive: true
      }
    ]
  };

  if (!viewBanners) return <Error404 />;

  const updateBannerStatus = (id, published) => {
    setBannersList(prev => prev.map(b => (b.id === id ? { ...b, 'published-at': published } : b)));
  };

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <div
        className="filter-bar pb-0"
        style={{ border: 'none', paddingBottom: 0 }}
      >
        <div className="filter-bar__inner">
          <div className="filter-bar__row">
            <label
              htmlFor="search"
              className="filter-bar__label"
            >
              {t('textsCommon.research')}
            </label>
            <input
              className="form__control form__control--search-with-icon"
              placeholder={t('bulletinBoards.searchBanner')}
              type="search"
              name="search"
              id="search"
              onChange={e => filters.setFieldValue('searchTerm', e.target.value)}
            />
          </div>
          <FilterSelectNative
            label={t('bulletinBoards.associateProfiles')}
            value={filters.values.profile}
            onChange={e => filters.setFieldValue('profile', e.target.value)}
            options={profiles.map(item => ({
              id: item.id,
              name: item.name
            }))}
          />
          <FilterSelectNative
            label="Status"
            value={filters.values.status}
            onChange={e => filters.setFieldValue('status', e.target.value)}
            options={[
              { id: 'published', name: 'Publicado' },
              { id: 'unpublished', name: 'Não publicado' }
            ]}
          />
        </div>
        {usePermission('Criar Mural', 'Mural') && (
          <div className="filter-bar__actions">
            <Link
              className="btn btn--primary btn--wide"
              to={`/banners/novo`}
            >
              {t('bulletinBoards.newBanner')}
            </Link>
          </div>
        )}
      </div>

      {!bannersList && loading && <Loader />}
      {!loading && bannersList && bannersList?.length === 0 && <EmptyState type="data" />}

      <InfiniteScroll
        dataLength={bannersList.length}
        next={() => {
          setPageNumber(pageNumber + 1);
        }}
        hasMore={hasMore}
        loader={<Loader />}
        endMessage={<EndScroll />}
      >
        <div className="round-dg-wrapper show-md">
          <table
            className="round-dg round-dg--light overflow-auto"
            style={{ minWidth: '990px' }}
          >
            <thead className="round-dg__header">
              <tr className="round-dg__row">
                <th className="round-dg__cell-header w-[350px]">Título do banner</th>
                <th className="round-dg__cell-header w-[200px]">Período</th>
                <th className="round-dg__cell-header w-[250px]">Perfis atribuídos</th>
                <th className="round-dg__cell-header w-[150px]">Publicação</th>
                <th className="round-dg__cell-header w-[80px]">Ações</th>
              </tr>
            </thead>
            <tbody className="round-dg__body">
              {bannersList.map(item => (
                <BannerItem
                  key={item.id}
                  banner={item}
                  setOpenDetailsModal={setOpenDetailsModal}
                  setSelectedBanner={setSelectedBanner}
                  updateBannerStatus={updateBannerStatus}
                  updateList={() => {
                    setBannersList(prev => prev.filter(b => b.id !== item.id));
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      </InfiniteScroll>

      <BannerDetailsModal
        open={openDetailsModal}
        onClose={() => setOpenDetailsModal(false)}
        banner={selectedBanner}
      />
    </DashboardContent>
  );
}
